// Comprehensive backend plan validation

import { CoordinateValidator, ValidationContext } from './CoordinateValidator';
import { ContentRect } from './CoordinateSpaces';

export interface BackendLayoutPlan {
  // Authoritative dimensions (required)
  pageSizePx: { width: number; height: number };
  contentRectPx: ContentRect;
  dpi: number;
  
  // Font verification (required)
  fontFamily: string;
  fontSizePx: number;
  features?: string; // "kern liga"
  scaleKind?: string;
  upem?: number;
  
  // Text positioning (required) - nested pages structure
  pages: Array<{
    width: number;
    height: number;
    frames: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      lines: Array<{
        baselineY_pagePx: number; // Absolute page Y coordinate
        runs: Array<{
          text: string;
          x: number; // Content space coordinate (backend uses 'x' instead of 'x_contentPx')
          charBoxes?: Array<[number, number, number, number]>; // Page-absolute coordinates [x, y, width, height]
          fontFamily?: string;
          fontSizePx?: number;
        }>;
      }>;
    }>;
  }>;
  
  // Metadata
  planHash?: string;
  schemaVersion?: string;
}

export interface FrontendRequest {
  pageSize?: string;
  pageWidth: number;
  pageHeight: number;
  margins: { top: number; right: number; bottom: number; left: number };
  dpi: number;
  fontFamily: string;
  fontSize: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  diff?: ValidationDiff;
  validationTime?: number;
}

export interface ValidationDiff {
  timestamp: string;
  expected: {
    pageWidth: number;
    pageHeight: number;
    contentWidth: number;
    contentHeight: number;
    margins: { top: number; right: number; bottom: number; left: number };
    fontFamily: string;
    fontSize: number;
    dpi: number;
  };
  actual: {
    pageWidth: number;
    pageHeight: number;
    contentWidth: number;
    contentHeight: number;
    contentRect: ContentRect;
    fontFamily: string;
    fontSize: number;
    features?: string;
    scaleKind?: string;
    upem?: number;
    lineCount: number;
  };
  validation: {
    hasRequiredFields: boolean;
    hasBaselines: boolean;
    hasSpaceFlags: boolean;
    hasValidFontInfo: boolean;
  };
}

export class BackendPlanValidator {
  private static readonly EPSILON = 0.5; // 0.5px tolerance

  /**
   * Comprehensive validation of backend layout plan
   */
  static validateComplete(
    plan: BackendLayoutPlan,
    request: FrontendRequest
  ): ValidationResult {
    const startTime = performance.now();
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // 1. Required fields validation
      const requiredFieldErrors = this.validateRequiredFields(plan);
      errors.push(...requiredFieldErrors);

      // 2. Coordinate system validation
      const coordinateValidation = CoordinateValidator.validatePlan({
        frontendRequest: request,
        backendPlan: plan
      });
      
      if (!coordinateValidation.isValid) {
        errors.push(...coordinateValidation.errors);
      }
      if (coordinateValidation.warnings) {
        warnings.push(...coordinateValidation.warnings);
      }

      // 3. Font parity validation
      const fontErrors = this.validateFontParity(plan, request);
      errors.push(...fontErrors);

      // 4. Baseline validation
      const baselineErrors = this.validateBaselines(plan);
      errors.push(...baselineErrors);

      // 5. Space flag validation
      const spaceErrors = this.validateSpaceFlags(plan);
      errors.push(...spaceErrors);

      // 6. Schema version validation
      const schemaWarnings = this.validateSchemaVersion(plan);
      warnings.push(...schemaWarnings);

      const validationTime = performance.now() - startTime;
      const isValid = errors.length === 0;

      if (!isValid) {
        const diff = this.generateStructuredDiff(plan, request);
        return { isValid: false, errors, warnings, diff, validationTime };
      }

      return { isValid: true, errors: [], warnings, validationTime };

    } catch (error) {
      const validationTime = performance.now() - startTime;
      errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return { isValid: false, errors, warnings, validationTime };
    }
  }

  /**
   * Validate all required fields are present
   */
  private static validateRequiredFields(plan: BackendLayoutPlan): string[] {
    const errors: string[] = [];

    if (!plan.pageSizePx?.width || !plan.pageSizePx?.height) {
      errors.push('Missing required pageSizePx dimensions');
    }

    if (!plan.contentRectPx) {
      errors.push('Missing required contentRectPx');
    } else {
      // Validate content rect structure
      if (typeof plan.contentRectPx.x !== 'number' || typeof plan.contentRectPx.y !== 'number') {
        errors.push('contentRectPx must have numeric x and y coordinates');
      }
      if (typeof plan.contentRectPx.width !== 'number' || typeof plan.contentRectPx.height !== 'number') {
        errors.push('contentRectPx must have numeric width and height');
      }
      if (plan.contentRectPx.width <= 0 || plan.contentRectPx.height <= 0) {
        errors.push('contentRectPx must have positive width and height');
      }
    }

    if (!plan.fontFamily || !plan.fontSizePx) {
      errors.push('Missing required font information (fontFamily and fontSizePx)');
    }

    const lines = plan.pages?.[0]?.frames?.[0]?.lines;
    if (!lines || !Array.isArray(lines)) {
      errors.push('Missing or invalid lines array');
    } else if (lines.length === 0) {
      errors.push('Lines array cannot be empty');
    }

    return errors;
  }

  /**
   * Validate font parity between frontend and backend
   */
  private static validateFontParity(
    plan: BackendLayoutPlan,
    request: FrontendRequest
  ): string[] {
    const errors: string[] = [];

    if (plan.fontFamily !== request.fontFamily) {
      errors.push(`Font family mismatch: expected "${request.fontFamily}", got "${plan.fontFamily}"`);
    }

    // Convert frontend font size (points) to pixels for comparison
    const expectedFontSizePx = request.fontSize * (request.dpi / 72);
    if (Math.abs(plan.fontSizePx - expectedFontSizePx) > 0.1) {
      errors.push(`Font size mismatch: expected ${expectedFontSizePx}px (${request.fontSize}pt at ${request.dpi} DPI), got ${plan.fontSizePx}px`);
    }

    // Validate required OpenType features if specified
    if (plan.features) {
      const requiredFeatures = ['kern', 'liga'];
      const planFeatures = plan.features.split(' ').filter(f => f.length > 0);
      
      for (const feature of requiredFeatures) {
        if (!planFeatures.includes(feature)) {
          errors.push(`Missing recommended OpenType feature: ${feature}`);
        }
      }
    }

    return errors;
  }

  /**
   * Validate baseline information is available
   */
  private static validateBaselines(plan: BackendLayoutPlan): string[] {
    const errors: string[] = [];
    const lines = plan.pages?.[0]?.frames?.[0]?.lines || [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (typeof line.baselineY_pagePx !== 'number') {
        errors.push(`Line ${i}: Missing or invalid baselineY_pagePx (got ${typeof line.baselineY_pagePx})`);
      } else if (line.baselineY_pagePx < 0) {
        errors.push(`Line ${i}: baselineY_pagePx cannot be negative (got ${line.baselineY_pagePx})`);
      }

      if (!line.runs || !Array.isArray(line.runs)) {
        errors.push(`Line ${i}: Missing or invalid runs array`);
      } else if (line.runs.length === 0) {
        errors.push(`Line ${i}: Runs array cannot be empty`);
      }
    }

    return errors;
  }

  /**
   * Validate coordinate space flags are present
   */
  private static validateSpaceFlags(plan: BackendLayoutPlan): string[] {
    const errors: string[] = [];

    // Validate content space coordinates are properly flagged
    const lines = plan.pages?.[0]?.frames?.[0]?.lines || [];
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      
      for (let runIndex = 0; runIndex < line.runs.length; runIndex++) {
        const run = line.runs[runIndex];
        
        if (typeof run.x !== 'number') {
          errors.push(`Line ${lineIndex}, Run ${runIndex}: Missing or invalid x coordinate (got ${typeof run.x})`);
        }

        // Allow empty text for empty documents, but text must be a string if present
        if (run.text !== undefined && run.text !== null && typeof run.text !== 'string') {
          errors.push(`Line ${lineIndex}, Run ${runIndex}: Invalid text content type (expected string, got ${typeof run.text})`);
        }
        
        // Only require non-empty text if document has actual content
        if (run.text === undefined || run.text === null) {
          // For now, allow missing text (empty document case)
          // TODO: This should be coordinated with backend to ensure proper text handling
        }

        // Validate character boxes if present
        if (run.charBoxes) {
          if (!Array.isArray(run.charBoxes)) {
            errors.push(`Line ${lineIndex}, Run ${runIndex}: charBoxes must be an array`);
          } else {
            run.charBoxes.forEach((box: [number, number, number, number], boxIndex: number) => {
              if (!Array.isArray(box) || box.length !== 4) {
                errors.push(`Line ${lineIndex}, Run ${runIndex}, Box ${boxIndex}: charBox must be [x, y, width, height] array`);
              } else {
                const [x, y, width, height] = box;
                if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number' || typeof height !== 'number') {
                  errors.push(`Line ${lineIndex}, Run ${runIndex}, Box ${boxIndex}: charBox values must be numbers`);
                }
              }
            });
          }
        }
      }
    }

    return errors;
  }

  /**
   * Validate schema version
   */
  private static validateSchemaVersion(plan: BackendLayoutPlan): string[] {
    const warnings: string[] = [];

    if (!plan.schemaVersion) {
      warnings.push('Missing schemaVersion - assuming latest version');
    } else if (plan.schemaVersion !== '1.0') {
      warnings.push(`Unknown schemaVersion "${plan.schemaVersion}" - expected "1.0"`);
    }

    return warnings;
  }

  /**
   * Generate structured diff for debugging
   */
  private static generateStructuredDiff(
    plan: BackendLayoutPlan,
    request: FrontendRequest
  ): ValidationDiff {
    const expectedContentWidth = request.pageWidth - request.margins.left - request.margins.right;
    const expectedContentHeight = request.pageHeight - request.margins.top - request.margins.bottom;

    return {
      timestamp: new Date().toISOString(),
      expected: {
        pageWidth: request.pageWidth,
        pageHeight: request.pageHeight,
        contentWidth: expectedContentWidth,
        contentHeight: expectedContentHeight,
        margins: request.margins,
        fontFamily: request.fontFamily,
        fontSize: request.fontSize,
        dpi: request.dpi
      },
      actual: {
        pageWidth: plan.pageSizePx?.width || 0,
        pageHeight: plan.pageSizePx?.height || 0,
        contentWidth: plan.contentRectPx?.width || 0,
        contentHeight: plan.contentRectPx?.height || 0,
        contentRect: plan.contentRectPx || { x: 0, y: 0, width: 0, height: 0 },
        fontFamily: plan.fontFamily || '',
        fontSize: plan.fontSizePx || 0,
        features: plan.features,
        scaleKind: plan.scaleKind,
        upem: plan.upem,
        lineCount: plan.pages?.[0]?.frames?.[0]?.lines?.length || 0
      },
      validation: {
        hasRequiredFields: !!(plan.pageSizePx && plan.contentRectPx && plan.fontFamily),
        hasBaselines: plan.pages?.[0]?.frames?.[0]?.lines?.every(line => typeof line.baselineY_pagePx === 'number') || false,
        hasSpaceFlags: plan.pages?.[0]?.frames?.[0]?.lines?.every(line => 
          line.runs?.every(run => typeof run.x === 'number')
        ) || false,
        hasValidFontInfo: !!(plan.fontFamily && plan.fontSizePx)
      }
    };
  }

  /**
   * Quick validation for performance-critical scenarios
   */
  static quickValidate(
    plan: BackendLayoutPlan,
    expectedPageWidth: number,
    expectedPageHeight: number
  ): boolean {
    if (!plan.pageSizePx) return false;
    
    const widthDelta = Math.abs(plan.pageSizePx.width - expectedPageWidth);
    const heightDelta = Math.abs(plan.pageSizePx.height - expectedPageHeight);
    
    return widthDelta <= this.EPSILON && heightDelta <= this.EPSILON;
  }

  /**
   * Validate specific aspects of a plan
   */
  static validateAspect(
    plan: BackendLayoutPlan,
    aspect: 'dimensions' | 'fonts' | 'baselines' | 'coordinates'
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    switch (aspect) {
      case 'dimensions':
        if (!plan.pageSizePx?.width || !plan.pageSizePx?.height) {
          errors.push('Invalid page dimensions');
        }
        if (!plan.contentRectPx || plan.contentRectPx.width <= 0 || plan.contentRectPx.height <= 0) {
          errors.push('Invalid content rectangle');
        }
        break;

      case 'fonts':
        if (!plan.fontFamily || !plan.fontSizePx) {
          errors.push('Missing font information');
        }
        break;

      case 'baselines':
        if (!plan.pages?.[0]?.frames?.[0]?.lines?.every(line => typeof line.baselineY_pagePx === 'number')) {
          errors.push('Missing or invalid baseline information');
        }
        break;

      case 'coordinates':
        if (!plan.pages?.[0]?.frames?.[0]?.lines?.every(line => 
          line.runs?.every(run => typeof run.x === 'number')
        )) {
          errors.push('Missing or invalid coordinate information');
        }
        break;
    }

    return { isValid: errors.length === 0, errors };
  }
}